import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withTranslation } from "react-i18next";
import moment from "moment";
import _ from "lodash";
import { Popover, Typography, Button, Space, Modal, Select, Tooltip, Spin } from "antd";
import { QuestionCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { ReactComponent as WarningIcon } from "../assets/svg/warning-icon-popup.svg";

import ACTIONS from "../store/action";
import { selectRecentPatients } from "@tailormed/common-client/store/reselectSelector";
import { selectIsFetching, selectSite, selectAssigneesAndUserGroups, selectPatientCycles } from "../store/selector";
import { ApiNames, searchFacilitiesAPI } from "../api/api";
import SupportIcon from "../assets/svg/SupportIcon";
import { DeceasedLabel } from "./DeceasedLabel";
import { PatientRecipientTable } from "./PatientRecipientTable";
import { MultiSelect, SingleSelect } from "./customComponent/CustomSelect";
import { uniqBy } from "lodash";
import { formatAssignee } from "../utils/formaters";
import { BLACK, WHITE, TRANSPARENT, SEC_FONT_GREY } from "../constant/colors";
import styled from "styled-components";
import { sortAssignees } from "../utils/sort";
import { dateTimeFormatter } from "../utils/date";
import { sizes } from "../constant/styles";

const { Text, Title } = Typography;
const ReachableContext = React.createContext();
const { Option } = Select;
const StyledMultiSelect = styled(MultiSelect)`
  .ant-select-selection-item {
    background-color: ${TRANSPARENT};
    border: none;
  }
`;

const StyledSingleSelect = styled(SingleSelect)`
  :not(:hover) {
    .ant-select-arrow {
      visibility: hidden !important;
    }
  }
`;

const DATE_TIME_FORMAT = "MM/DD/YYYY h:mm a";

const FACILITY_DEBOUNCE_MS = 400;
const FACILITY_MIN_SEARCH_CHARS = 3;

const PatientModalHeaderComponent = ({
  t,
  patient,
  site,
  cycles,
  recentPatients,
  setShowSupport,
  setPharmacyPatientAct,
  clonePharmacyPatientAct,
  isFeching,
  assignees,
  bulkChangeAct,
  isProcessingPatient
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [patientAssignees, setPatientAssignees] = useState([]);
  const [isSelectHover, setIsSelectHover] = useState(false);
  const [isAssigneeSelectOpen, setIsAssigneeSelectOpen] = useState(false);
  const [modal, contextHolder] = Modal.useModal();
  const { isActive, facility, facilityName, assignees: currentAssignees } = patient;

  // Facility search state
  const [facilitySearchText, setFacilitySearchText] = useState("");
  const [facilitySearchResults, setFacilitySearchResults] = useState([]);
  const [isFacilityFetching, setIsFacilityFetching] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(undefined);

  useEffect(() => {
    if (facility) {
      setSelectedFacility({ value: String(facility), label: facilityName || String(facility) });
    }
  }, [facility, facilityName]);

  const debouncedFacilitySearch = useRef(
    _.debounce(async (query) => {
      if (query.length < FACILITY_MIN_SEARCH_CHARS) {
        setFacilitySearchResults([]);
        setIsFacilityFetching(false);
        return;
      }
      try {
        const response = await searchFacilitiesAPI(query);
        setFacilitySearchResults(response?.data?.providers || []);
      } catch (error) {
        setFacilitySearchResults([]);
      } finally {
        setIsFacilityFetching(false);
      }
    }, FACILITY_DEBOUNCE_MS)
  ).current;

  useEffect(() => {
    return () => debouncedFacilitySearch.cancel();
  }, [debouncedFacilitySearch]);

  const handleFacilitySearch = useCallback(
    (value) => {
      setFacilitySearchText(value);
      if (value.length < FACILITY_MIN_SEARCH_CHARS) {
        setFacilitySearchResults([]);
        setIsFacilityFetching(false);
        return;
      }
      setIsFacilityFetching(true);
      debouncedFacilitySearch(value);
    },
    [debouncedFacilitySearch]
  );

  const getFacilityNotFoundContent = () => {
    if (facilitySearchText.length < FACILITY_MIN_SEARCH_CHARS) return t("type_to_search");
    if (isFacilityFetching) return <Spin size="small" indicator={<LoadingOutlined spin />} />;
    return undefined;
  };
  const assigneesForFilter = Object.keys(assignees).map((key) => ({
    text: formatAssignee(assignees[key]),
    value: assignees[key].id
  }));
  const assigneesIds = (patientAssignees?.length && patientAssignees.map(({ id }) => id)) || [];

  const assigneesTextToDisplay =
    patientAssignees && patientAssignees.length
      ? patientAssignees.map((assignee) => formatAssignee(assignee)).join(" | ")
      : "";

  useEffect(() => {
    if (!assignees || !Object.keys(assignees).length || !currentAssignees || !currentAssignees.length) return;

    const patientAssignees = currentAssignees
      .map(({ id: currentId }) => {
        const chosenAssignee = Object.values(assignees).find(({ id }) => id && id.toString() === currentId.toString());
        if (chosenAssignee) return chosenAssignee;
      })
      .sort((a, b) => {
        return formatAssignee(a).localeCompare(formatAssignee(b));
      });

    setPatientAssignees(patientAssignees.filter((item) => item));
  }, [currentAssignees, assignees]);

  // Add this user to the recent patients array
  useEffect(() => {
    if (patient && recentPatients?.patients?.length) {
      dispatch(ACTIONS.addRecentPatientsAction(recentPatients, patient, "pharmacy"));
    }
  }, [patient]);

  const updateAssigneeHandler = (values) => {
    const newAssignees = values.map((value) => {
      const selectedAssignee = Object.values(assignees).find(({ id }) => id && id.toString() === value.toString());
      if (selectedAssignee) {
        return selectedAssignee;
      }
    });
    setPatientAssignees(newAssignees.filter((item) => item));
    bulkChangeAct(
      {
        id: patient.id,
        pharmacyPatient: patient.id,
        assignees: newAssignees.map(({ id }) => id)
      },
      cycles
    );
  };
  const updateFacilityHandler = (value) => {
    bulkChangeAct(
      {
        id: patient.id,
        pharmacyPatient: patient.id,
        facility: value || null
      },
      cycles
    );
  };

  useEffect(() => {
    if (!isFeching) {
      setIsLoading(false);
    }
  }, [isFeching]);

  const onClickSetActive = (isActive) => {
    setIsLoading(true);
    const pharmacyPatient = {
      id: patient.id,
      isActive
    };
    setPharmacyPatientAct(pharmacyPatient);
  };

  const onClickClone = () => {
    modal.confirm({
      centered: true,
      className: "formPopupsModal",
      icon: <WarningIcon />,
      okText: t("yes_sure"),
      cancelText: t("back"),
      title: t("patient.are_you_sure_clone_title"),
      content: t("patient.are_you_sure_clone_content"),
      onOk: () => {
        clonePharmacyPatientAct(patient.id);
      }
    });
  };

  const {
    id,
    mrn,
    first_name,
    middle_name,
    last_name,
    dob,
    isDeceased,
    address,
    state_code,
    zip_code,
    city,
    email,
    phone_number,
    social_security_number
  } = patient.patient;

  const { updatedAt, updatedBy } = patient;

  const patientAddress =
    address && city && state_code && zip_code ? `${address}, ${city}, ${state_code}, ${zip_code}` : address || null;

  const patientPhone = phone_number;
  const patientEmail = email;
  const socialSecurityNumber = social_security_number;
  const mrnDisplay = displayedMRN(mrn);

  return (
    <ReachableContext.Provider value={null}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          borderBottom: `1.5px solid #9B9B9B`,
          paddingBottom: 8
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            {isDeceased && <DeceasedLabel />}
            <Title
              data-tm-id="patient-pg-name"
              id="patient_pg_name"
              style={{ paddingLeft: isDeceased ? 4 : 0, margin: 0 }}
              level={5}
            >
              {`${
                first_name && last_name
                  ? `${first_name}${middle_name ? ` ${middle_name}` : ""} ${last_name}`
                  : `Patient`
              } (${site}-${id}) `}
            </Title>
            <Popover
              id="patient-info-popover"
              content={PatientRecipientTable({
                t,
                dataSource: [{ patientPhone, patientEmail, patientAddress, socialSecurityNumber, key: id }]
              })}
              trigger="click"
            >
              <QuestionCircleOutlined id="patient-info-tooltip-icon" style={{ paddingLeft: "5px" }} />
            </Popover>
            <Button
              style={{ marginLeft: 6 }}
              loading={isLoading}
              onClick={() => (isActive ? onClickSetActive(false) : onClickSetActive(true))}
              data-tm-id="patient-pg-In-active-btn"
              id="cycle-activision-toggle"
            >
              {isActive ? t("patient.set_inactive") : t("patient.activate")}
            </Button>
            <Button
              data-tm-id="patient-pg-clone-btn"
              id="clone-patient"
              style={{ marginLeft: 6 }}
              loading={isLoading}
              onClick={() => onClickClone()}
            >
              {t("patient.clone")}
            </Button>
            {updatedAt && moment(updatedAt).isValid() && (
              <Space style={{ marginLeft: 20 }}>
                <Text
                  style={{
                    fontSize: sizes.medium,
                    color: SEC_FONT_GREY
                  }}
                >
                  {t("last_updated")}
                </Text>
                <Text style={{ fontSize: sizes.medium }} id="patient_pg_updated_by">
                  {dateTimeFormatter(updatedAt, DATE_TIME_FORMAT)}
                  {updatedBy &&
                    t("last_updated_by", {
                      updatedBy
                    })}
                </Text>
              </Space>
            )}
            <Button
              data-tm-id="patient-pg-contact-btn"
              type="text"
              onClick={() => setShowSupport(true)}
              style={{
                marginInlineStart: "auto",
                display: "inline-block"
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <SupportIcon />
                <Text style={{ paddingLeft: 4, color: "#2B56A3" }}>{t("contact_support")}</Text>
              </div>
            </Button>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "start",
              width: "100%"
            }}
          >
            {mrnDisplay && (
              <Space>
                <Text style={{ fontSize: 14, color: "#9B9B9B" }}>{"MRN"}</Text>
                <Text style={{ fontSize: 14 }} id="patient_pg_mrn">
                  {mrnDisplay}
                </Text>
              </Space>
            )}
            {dob && moment(dob)._isValid && (
              <Space>
                <Text style={{ fontSize: 14, paddingLeft: mrnDisplay ? 20 : 0, color: "#9B9B9B" }}>{"Born"}</Text>
                <Text style={{ fontSize: 14 }} id="patient_pg_dob">
                  {moment(dob).format("MMM DD, YYYY")}
                </Text>
              </Space>
            )}
            <Space>
              <Text style={{ fontSize: 14, paddingLeft: mrnDisplay || dob ? 20 : 0, color: "#9B9B9B" }}>
                {t("form.facility.label")}
              </Text>
              <StyledSingleSelect
                value={selectedFacility}
                labelInValue
                id={t("form.facility.id")}
                placeholder={t("form.facility.placeholder")}
                dataTMId={"patient-pg-facility"}
                allowClear={true}
                showSearch={true}
                filterOption={false}
                onSearch={handleFacilitySearch}
                style={{
                  borderWidth: 0,
                  color: BLACK
                }}
                onChange={(labeledValue) => {
                  if (!labeledValue) {
                    setSelectedFacility(undefined);
                    setFacilitySearchResults([]);
                    setFacilitySearchText("");
                    updateFacilityHandler(null);
                    return;
                  }
                  const selected = facilitySearchResults.find((f) => String(f.id) === String(labeledValue.value));
                  if (selected) {
                    setSelectedFacility({ value: String(selected.id), label: selected.name });
                    updateFacilityHandler(selected.id);
                  }
                }}
                dropdownMatchSelectWidth={false}
                getPopupContainer={(triggerNode) => triggerNode.closest("#patient_card")}
                notFoundContent={getFacilityNotFoundContent()}
                optionLabelProp="label"
              >
                {facilitySearchResults.map((fac) => (
                  <Option key={String(fac.id)} value={String(fac.id)} label={fac.name}>
                    {fac.name}
                  </Option>
                ))}
              </StyledSingleSelect>
            </Space>
            <Space>
              <Text style={{ fontSize: 14, paddingLeft: 20, color: "#9B9B9B" }}>{t("form.assignee.label")}</Text>
              <Tooltip
                open={assigneesTextToDisplay.length && !isAssigneeSelectOpen && isSelectHover && !isProcessingPatient}
                title={<Text>{assigneesTextToDisplay}</Text>}
                color={WHITE}
                style={{ cursor: "pointer" }}
              >
                <StyledMultiSelect
                  dataTMId="cct-assignee"
                  value={patientAssignees.map(({ id }) => id)}
                  id={t("form.assignee.id")}
                  placeholder={t("form.assignee.placeholder")}
                  allowClear={false}
                  showArrow={isSelectHover}
                  filterSort={(a, b) => sortAssignees(a, b, assigneesIds)}
                  onChange={(value) => updateAssigneeHandler(value)}
                  getPopupContainer={(triggerNode) => triggerNode.closest("#patient_card")}
                  style={{
                    paddingRight: 10,
                    minWidth: 140,
                    borderWidth: 0,
                    color: BLACK
                  }}
                  onDropdownVisibleChange={(open) => setIsAssigneeSelectOpen(open)}
                  onMouseEnter={() => setIsSelectHover(true)}
                  onMouseLeave={() => setIsSelectHover(false)}
                  dropdownMatchSelectWidth={false}
                >
                  {uniqBy(assigneesForFilter, "value").map((assignee, index) => (
                    <Option key={index} value={assignee.value}>
                      {assignee.text}
                    </Option>
                  ))}
                </StyledMultiSelect>
              </Tooltip>
            </Space>
          </div>
        </div>
      </div>
      {contextHolder}
    </ReachableContext.Provider>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setPharmacyPatientAct: (pharmacyPatient) => dispatch(ACTIONS.setPharmacyPatientAction(pharmacyPatient)),
  clonePharmacyPatientAct: (pharmacyPatientId) => dispatch(ACTIONS.clonePharmacyPatientAction(pharmacyPatientId)),
  bulkChangeAct: (pharmacyPatient, cycles) => dispatch(ACTIONS.bulkChangeAction(pharmacyPatient, cycles))
});

const mapStateToProps = createStructuredSelector({
  assignees: selectAssigneesAndUserGroups,
  site: selectSite,
  cycles: selectPatientCycles,
  recentPatients: selectRecentPatients,
  isFeching: selectIsFetching([ApiNames.PatientPhiInfo, ApiNames.PharmacyPatients]),
  isProcessingPatient: selectIsFetching([ApiNames.BulkChange])
});

const PatientModalHeader = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientModalHeaderComponent));

export { PatientModalHeader };
